"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { auth, db } from "../config/firebase"
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
} from "firebase/auth"
import { doc, getDoc } from "firebase/firestore"

interface AuthContextType {
  user: User | null
  loading: boolean
  apiEndpointConfigured: boolean
  apiEndpoint: string | null
  signIn: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  checkApiEndpoint: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [apiEndpointConfigured, setApiEndpointConfigured] = useState(false)
  const [apiEndpoint, setApiEndpoint] = useState<string | null>(null)

  useEffect(() => {
    // Setting up auth state listener
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // AuthProvider: Auth state changed

      setUser(currentUser)

      if (currentUser) {
        // Check if the user has configured an API endpoint
        await checkApiEndpoint()
      } else {
        setApiEndpointConfigured(false)
        setApiEndpoint(null)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [])

  async function checkApiEndpoint(): Promise<boolean> {
    if (!user) return false

    try {
      // Checking API endpoint for user
      const userSettingsDoc = await getDoc(doc(db, "userSettings", user.uid))

      if (userSettingsDoc.exists() && userSettingsDoc.data().apiEndpoint) {
        const endpoint = userSettingsDoc.data().apiEndpoint
        // Found API endpoint in Firestore

        // Validate the endpoint by making a test request
        try {
          // Validating endpoint
          const response = await fetch(`${endpoint}assets`)

          if (response.ok) {
            // Only set as configured if the endpoint is valid
            // Endpoint is valid, updating state
            setApiEndpoint(endpoint)
            setApiEndpointConfigured(true)
            return true
          } else {
            // API endpoint exists but returned an error
            setApiEndpointConfigured(false)
            setApiEndpoint(null)
            return false
          }
        } catch (error) {
          // API endpoint exists but could not be reached
          setApiEndpointConfigured(false)
          setApiEndpoint(null)
          return false
        }
      } else {
        // No API endpoint found for user
        setApiEndpointConfigured(false)
        setApiEndpoint(null)
        return false
      }
    } catch (error) {
      // Error checking API endpoint
      setApiEndpointConfigured(false)
      setApiEndpoint(null)
      return false
    }
  }

  async function signIn(email: string, password: string) {
    try {
      // Signing in user
      await signInWithEmailAndPassword(auth, email, password)
      // After sign in, check API endpoint configuration
      if (auth.currentUser) {
        // User signed in, checking API endpoint
        await checkApiEndpoint()
      }
    } catch (error: any) {
      throw new Error(error.message)
    }
  }

  async function logout() {
    try {
      // Logging out user
      await signOut(auth)
      setApiEndpointConfigured(false)
      setApiEndpoint(null)
    } catch (error: any) {
      throw new Error(error.message)
    }
  }

  async function resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (error: any) {
      throw new Error(error.message)
    }
  }

  const value = {
    user,
    loading,
    apiEndpointConfigured,
    apiEndpoint,
    signIn,
    logout,
    resetPassword,
    checkApiEndpoint,
  }

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>
}

